import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import Card from '../../../../../molecules/Card'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../../commonDesign/SydCheckbox'
import SydButton from '../../../../../commonDesign/Button'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'
import { decipherError } from '../../../../../../utils/decipherError'
import {
  KEY_TYPE_OPTIONS,
  sectionHeaderStyle,
  rowStyle,
  errorStyle,
  emptyStyle,
  descriptionStyle,
  getDefinition,
  normalizeKeyArray,
  useSaveDefinitionSection
} from './shared'

const sectionDescriptions = {
  partitionKey: 'The partition key determines how data is physically divided across MySQL LIST partitions. Each enabled job\'s connection gets its own partition. These columns are added to the table automatically as NOT NULL physical columns and form the first part of the composite primary key.',
  domainKey: 'The domain key uniquely identifies a row within a partition. Together with the partition key, it forms the composite primary key. These columns are added to the table automatically as NOT NULL physical columns. You do not need to redefine them in the columns section.'
}

function KeyColumnRow ({ item, index, onChange, onRemove, canRemove }) {
  const showLength = item.type === 'string'
  return (
    <div style={rowStyle}>
      <Grid container spacing={1} alignItems='flex-end'>
        <Grid item xs={3}>
          <SydLabel label='Name'>
            <SydInput size='sm' value={item.name} onChange={e => onChange(index, 'name', e.target.value)} placeholder='column_name' minWidth='0' />
          </SydLabel>
        </Grid>
        <Grid item xs={2}>
          <SydLabel label='Type'>
            <SydSelect size='sm' options={KEY_TYPE_OPTIONS} value={item.type} onChange={e => onChange(index, 'type', e.target.value)} minWidth='0' />
          </SydLabel>
        </Grid>
        <Grid item xs={showLength ? 3 : 4}>
          <SydLabel label='Path'>
            <SydInput size='sm' value={item.path || ''} onChange={e => onChange(index, 'path', e.target.value)} placeholder='$.field' minWidth='0' />
          </SydLabel>
        </Grid>
        {showLength && (
          <Grid item xs={1}>
            <SydLabel label='Length'>
              <SydInput size='sm' value={item.length ?? ''} onChange={e => onChange(index, 'length', e.target.value)} placeholder='255' minWidth='0' />
            </SydLabel>
          </Grid>
        )}
        <Grid item xs={2}>
          <SydCheckbox label='Not Null' value={item.notNull} onChange={(_, v) => onChange(index, 'notNull', v)} size='small' />
        </Grid>
        <Grid item xs={1} style={{ textAlign: 'right' }}>
          {canRemove && (
            <IconButton size='small' onClick={() => onRemove(index)} title='Remove'>
              <Icon name={ICON_NAMES.close} customSize='16px' />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

KeyColumnRow.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool
}

function EditKeyColumnsModal ({ open, onClose, target, sectionKey, title }) {
  const saveSection = useSaveDefinitionSection(target)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (open) {
      const def = getDefinition(target)
      const items = normalizeKeyArray(def[sectionKey])
      setRows(items.map(k => ({
        name: k.name || '',
        type: k.type || 'int',
        path: k.path || '',
        length: k.length ?? null,
        notNull: k.notNull !== false
      })))
      setError(null)
    }
  }, [open, target, sectionKey])

  const handleChange = useCallback((i, field, value) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r))
  }, [])

  const handleAdd = useCallback(() => {
    setRows(prev => [...prev, { name: '', type: 'int', path: '', length: null, notNull: true }])
  }, [])

  const handleRemove = useCallback((i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i))
  }, [])

  const handleSave = useCallback(async () => {
    if (!rows.length) { setError('At least one column is required'); return }
    for (const r of rows) { if (!r.name) { setError('All columns must have a name'); return } }

    const payload = rows.map(r => ({
      name: r.name,
      type: r.type,
      path: r.path || null,
      length: r.type === 'string' && r.length ? Number(r.length) : null,
      notNull: r.notNull
    }))

    try {
      setError(null)
      setProcessing(true)
      const value = sectionKey === 'domainKey' && payload.length === 1 ? payload[0] : payload
      await saveSection({ [sectionKey]: value })
      onClose?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [rows, saveSection, sectionKey, onClose])

  return (
    <SydModal open={open} onClose={onClose} title={`Edit ${title}`} size='large'>
      {sectionDescriptions[sectionKey] && (
        <div style={descriptionStyle}>{sectionDescriptions[sectionKey]}</div>
      )}
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
        <SydButton variant='outline' size='sm' icon='add' onClick={handleAdd}>Add Column</SydButton>
      </div>
      {rows.map((item, i) => (
        <KeyColumnRow key={i} item={item} index={i} onChange={handleChange} onRemove={handleRemove} canRemove={rows.length > 1} />
      ))}
      {!rows.length && <div style={emptyStyle}>No columns defined</div>}
      <SydModalActions>
        {error ? <div style={errorStyle}>{error}</div> : null}
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onClose}>Cancel</SydButton>
        <SydButton processing={processing} variant='primary' size='md' onClick={handleSave}>Save</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

EditKeyColumnsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  target: PropTypes.object,
  sectionKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
}

function KeyColumnCard ({ target, sectionKey, title }) {
  const [editing, setEditing] = useState(false)
  const definition = getDefinition(target)
  const items = normalizeKeyArray(definition[sectionKey])

  return (
    <Card>
      <div style={sectionHeaderStyle}>
        <h3>{title} ({items.length})</h3>
        <IconButton size='small' onClick={() => setEditing(true)} title={`Edit ${title}`}>
          <Icon name={ICON_NAMES.edit} customSize='18px' />
        </IconButton>
      </div>
      <table className='opal-table'>
        <thead>
          <tr><th>Name</th><th>Type</th><th>Path</th><th>Not Null</th></tr>
        </thead>
        <tbody>
          {items.map((k, i) => (
            <tr key={i}>
              <td><code>{k.name}</code></td>
              <td>{k.type}</td>
              <td>{k.path || '—'}</td>
              <td>{k.notNull !== false ? 'Yes' : 'No'}</td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={4}>No columns defined</td></tr>}
        </tbody>
      </table>
      <EditKeyColumnsModal open={editing} onClose={() => setEditing(false)} target={target} sectionKey={sectionKey} title={title} />
    </Card>
  )
}

KeyColumnCard.propTypes = {
  target: PropTypes.object.isRequired,
  sectionKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
}

export function PartitionKeyCard ({ target }) {
  return <KeyColumnCard target={target} sectionKey='partitionKey' title='Partition Key' />
}
PartitionKeyCard.propTypes = { target: PropTypes.object.isRequired }

export function DomainKeyCard ({ target }) {
  return <KeyColumnCard target={target} sectionKey='domainKey' title='Domain Key' />
}
DomainKeyCard.propTypes = { target: PropTypes.object.isRequired }
